import React from 'react';
import { Box, Menu, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { HEADER_CONTROL_TYPOGRAPHY } from 'app/pages/Header/constants/headerControlTypography';
import {
  HEADER_DROPDOWN_ANCHOR_ORIGIN,
  HEADER_DROPDOWN_MENU_BODY_SX,
  HEADER_DROPDOWN_MENU_LIST_SX,
  HEADER_DROPDOWN_MENU_SX,
  HEADER_DEALER_MENU_SX,
  HEADER_DROPDOWN_TRANSFORM_ORIGIN,
  HEADER_DROPDOWN_TRIGGER_SX,
} from './headerDropdownStyles';

export type HeaderDropdownMAIQProps = {
  id?: string;
  label: React.ReactNode;
  open: boolean;
  anchorEl: HTMLElement | null;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  disabled?: boolean;
  minWidth?: number | string;
  menuMinWidth?: number | string;
  menuMaxHeight?: number | string;
  /** When false, trigger click runs onTriggerClick only (no menu). Default true. */
  openOnTrigger?: boolean;
  /** When true, only the chevron opens the menu; label area runs onTriggerClick. */
  openOnChevronOnly?: boolean;
  onTriggerClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Use Box wrapper for stacked menu items. Set false for flat dealer lists. */
  wrapMenuChildren?: boolean;
  menuVariant?: 'default' | 'dealer';
  menuBodySx?: SystemStyleObject<Theme>;
  /** When true, label is rendered in a flex Box (icons + text). */
  richLabel?: boolean;
  /** Optional node rendered before the chevron (e.g. profile avatar on the right). */
  triggerEndAdornment?: React.ReactNode;
  /** Show dropdown chevron on the trigger. Default true. */
  showChevron?: boolean;
  /** Optional sx overrides for the trigger container. */
  triggerSx?: SystemStyleObject<Theme>;
  'aria-label'?: string;
  className?: string;
  children?: React.ReactNode;
};

export const HeaderDropdownMAIQ: React.FC<HeaderDropdownMAIQProps> = ({
  id,
  label,
  open,
  anchorEl,
  onOpen,
  onClose,
  disabled = false,
  minWidth,
  menuMinWidth = '200px',
  menuMaxHeight,
  openOnTrigger = true,
  openOnChevronOnly = false,
  onTriggerClick,
  wrapMenuChildren = true,
  menuVariant = 'default',
  menuBodySx,
  richLabel = false,
  triggerEndAdornment,
  showChevron = true,
  triggerSx,
  'aria-label': ariaLabel,
  className,
  children,
}) => {
  const triggerId = id ? `${id}-trigger` : undefined;
  const menuId = id ? `${id}-menu` : undefined;
  const triggerRef = React.useRef<HTMLElement>(null);

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    onTriggerClick?.(event);
    if (openOnTrigger) {
      onOpen(event);
    }
  };

  const handleLabelClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (disabled) return;
    onTriggerClick?.(event);
  };

  const handleChevronClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (disabled || !triggerRef.current) return;
    onOpen({
      ...event,
      currentTarget: triggerRef.current,
    } as React.MouseEvent<HTMLElement>);
  };

  const isSplitTrigger = openOnChevronOnly && showChevron;

  const resolvedMenuBodySx: SystemStyleObject<Theme> = menuBodySx ?? HEADER_DROPDOWN_MENU_BODY_SX;
  const resolvedMenuSx =
    menuVariant === 'dealer' ? HEADER_DEALER_MENU_SX : HEADER_DROPDOWN_MENU_SX;
  const isDealerMenu = menuVariant === 'dealer';

  return (
    <>
      <Box
        ref={triggerRef}
        id={triggerId}
        className={className}
        role={isSplitTrigger ? undefined : 'button'}
        tabIndex={isSplitTrigger ? undefined : disabled ? -1 : 0}
        aria-label={isSplitTrigger ? undefined : ariaLabel}
        aria-haspopup={isSplitTrigger ? undefined : 'listbox'}
        aria-expanded={isSplitTrigger ? undefined : open ? 'true' : 'false'}
        aria-controls={isSplitTrigger ? undefined : open ? menuId : undefined}
        aria-disabled={disabled ? 'true' : undefined}
        onClick={isSplitTrigger ? undefined : handleTriggerClick}
        onKeyDown={
          isSplitTrigger
            ? undefined
            : event => {
                if (disabled) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpen(event as unknown as React.MouseEvent<HTMLElement>);
                }
              }
        }
        sx={{
          ...HEADER_DROPDOWN_TRIGGER_SX,
          ...(minWidth != null ? { minWidth } : {}),
          ...(triggerSx ?? {}),
        }}
      >
        <Typography
          component="span"
          role={isSplitTrigger ? 'button' : undefined}
          tabIndex={isSplitTrigger ? (disabled ? -1 : 0) : undefined}
          aria-label={isSplitTrigger ? ariaLabel : undefined}
          onClick={isSplitTrigger ? handleLabelClick : undefined}
          onKeyDown={
            isSplitTrigger
              ? event => {
                  if (disabled) return;
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onTriggerClick?.(
                      event as unknown as React.MouseEvent<HTMLElement>,
                    );
                  }
                }
              : undefined
          }
          sx={{
            ...HEADER_CONTROL_TYPOGRAPHY,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            ...(isSplitTrigger ? { cursor: 'pointer' } : {}),
            ...(richLabel
              ? {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }
              : {}),
          }}
        >
          {label}
        </Typography>
        {triggerEndAdornment}
        {showChevron ? (
          <Box
            component="span"
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={isSplitTrigger ? 'Open menu' : undefined}
            aria-haspopup="listbox"
            aria-expanded={open ? 'true' : 'false'}
            aria-controls={open ? menuId : undefined}
            onClick={isSplitTrigger ? handleChevronClick : undefined}
            onKeyDown={
              isSplitTrigger
                ? event => {
                    if (disabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleChevronClick(
                        event as unknown as React.MouseEvent<HTMLElement>,
                      );
                    }
                  }
                : undefined
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              cursor: 'pointer',
            }}
          >
            <ArrowDropDownIcon
              sx={{
                color: 'inherit',
                fontSize: 24,
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s ease',
              }}
            />
          </Box>
        ) : null}
      </Box>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        autoFocus={false}
        anchorOrigin={HEADER_DROPDOWN_ANCHOR_ORIGIN}
        transformOrigin={HEADER_DROPDOWN_TRANSFORM_ORIGIN}
        MenuListProps={{
          'aria-labelledby': triggerId,
          sx: HEADER_DROPDOWN_MENU_LIST_SX,
        }}
        PaperProps={{
          sx: {
            width: isDealerMenu ? menuMinWidth : undefined,
            minWidth: isDealerMenu ? undefined : menuMinWidth,
            maxHeight: menuMaxHeight,
            mt: isDealerMenu ? 1 : undefined,
          },
        }}
        sx={resolvedMenuSx}
      >
        {children != null ? (
          wrapMenuChildren && !isDealerMenu ? (
            <Box sx={resolvedMenuBodySx}>{children}</Box>
          ) : (
            children
          )
        ) : null}
      </Menu>
    </>
  );
};

export default HeaderDropdownMAIQ;
